import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { BrandHeader } from "@/components/BrandHeader";
import {
  LayoutDashboard, Newspaper, Users, Mail, Settings as SettingsIcon,
  LogOut, KeyRound, ListChecks, CalendarRange, Plus, ChevronRight, ChevronDown, ShieldCheck, CalendarDays, Handshake, CreditCard, Archive, FileCheck2, Receipt, GalleryHorizontal, Frame,
} from "lucide-react";

const SECTION = "pt-5 pb-1 px-3 text-[10px] font-semibold tracking-[0.2em] text-slate-400";

const navBase =
  "nav-link flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-300 hover:bg-white/10 hover:text-white";
const navActive = "nav-active text-white hover:text-white";

export default function Sidebar({ open = false }) {
  const { user, logout } = useAuth();
  const perm = (a) => !!user?.permissions?.[a] && user.permissions[a] !== "none";
  const navigate = useNavigate();
  const location = useLocation();
  const [issues, setIssues] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith("/einstellungen") || location.pathname.startsWith("/benutzer") || location.pathname.startsWith("/mails"));
  const [archiveOpen, setArchiveOpen] = useState(false);

  const loadIssues = async () => {
    try {
      const { data } = await api.get("/issues");
      setIssues(data);
    } catch {}
  };
  useEffect(() => { loadIssues(); }, [location.pathname]);
  const activeIssues = issues.filter((i) => i.status !== "archiv");
  const archivedIssues = issues.filter((i) => i.status === "archiv");
  const issueLink = (i) => (
    <NavLink key={i.id} to={`/ausgabe/${i.id}`} className={({ isActive }) => `${navBase} justify-between ${isActive ? navActive : ""}`} data-testid={`nav-issue-${i.id}`}>
      <span className="flex items-center gap-3 min-w-0"><Newspaper size={18} /> <span className="truncate">{i.name}</span></span>
      <ChevronRight size={14} />
    </NavLink>
  );

  const initials = (user?.name || user?.email || "?")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <aside
      data-testid="sidebar-nav"
      className={`w-64 min-w-[16rem] shrink-0 h-screen flex flex-col brand-sidebar border-r border-white/10 fixed inset-y-0 left-0 z-50 transition-transform duration-200 md:sticky md:top-0 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
    >
      <div className="px-5 pt-6 pb-4">
        <BrandHeader />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
        {perm("dashboard") && <NavLink to="/" end className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-dashboard">
          <LayoutDashboard size={18} /> Dashboard
        </NavLink>}

        {(perm("ausgaben") || perm("bezahlkunden") || perm("buchhaltung")) && <div className={SECTION}>CLUB-MAGAZIN</div>}
        {perm("ausgaben") && <>
        <div className="pt-1 pb-1 px-3 text-[11px] text-slate-400 flex items-center justify-between">
          Ausgaben
          <button onClick={() => navigate("/")} className="text-slate-400 hover:text-white" data-testid="nav-add-issue">
            <Plus size={14} />
          </button>
        </div>
        {activeIssues.map(issueLink)}
        {activeIssues.length === 0 && (
          <div className="px-3 text-xs text-slate-500 italic">Keine aktive Ausgabe</div>
        )}
        {archivedIssues.length > 0 && <>
          <button onClick={() => setArchiveOpen((o) => !o)} className={`${navBase} w-full justify-between text-xs`} data-testid="nav-issue-archive-toggle">
            <span className="flex items-center gap-3"><Archive size={16} /> Archiv ({archivedIssues.length})</span>
            <ChevronDown size={14} className={`transition-transform ${archiveOpen ? "rotate-180" : ""}`} />
          </button>
          {archiveOpen && <div className="ml-3 pl-2 border-l border-white/10 space-y-1">{archivedIssues.map(issueLink)}</div>}
        </>}
        </>}

        {perm("bezahlkunden") && <NavLink to="/anzeigenkunden" end className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-paying-customers">
          <CreditCard size={18} /> Anzeigenkunden
        </NavLink>}
        {perm("bezahlkunden") && <NavLink to="/anzeigenkunden/anfragen" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-ad-requests">
          <FileCheck2 size={18} /> Anzeigen-Anfragen
        </NavLink>}
        {perm("buchhaltung") && <NavLink to="/buchhaltung" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-accounting">
          <Receipt size={18} /> Buchhaltung
        </NavLink>}

        {perm("partner") && <div className={SECTION}>PARTNER / SPONSOREN</div>}
        {perm("partner") && <NavLink to="/partner" end className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-partners">
          <Handshake size={18} /> Partner / Sponsoren
        </NavLink>}
        {perm("partner") && <NavLink to="/partner/anzeigen" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-ad-workflow">
          <FileCheck2 size={18} /> Anzeigen-Workflow
        </NavLink>}
        {perm("partner") && <NavLink to="/partner/belegungsplan" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-occupancy-plan">
          <CalendarRange size={18} /> Belegungsplan
        </NavLink>}
        {perm("partner") && <NavLink to="/partner/werbebanner" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-banner-planner">
          <GalleryHorizontal size={18} /> Werbebanner
        </NavLink>}
        {perm("partner") && <NavLink to="/partner/backdrops" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-backdrops">
          <Frame size={18} /> Backdrops
        </NavLink>}
        {user?.role === "admin" && <NavLink to="/partner/leistungskatalog" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-service-catalog">
          <ListChecks size={18} /> Leistungskatalog
        </NavLink>}

        <div className={SECTION}>EINSTELLUNGEN</div>
        {user?.role !== "admin" && perm("mails") && <NavLink to="/mails" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-emails">
          <Mail size={18} /> Mailvorlagen
        </NavLink>}
        {user?.role !== "admin" && perm("mitwirkende") && <NavLink to="/einstellungen/mitwirkende" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-people">
          <ShieldCheck size={18} /> Mitwirkende
        </NavLink>}
        {user?.role !== "admin" && perm("einstellungen") && <NavLink to="/einstellungen/anzeigenformate" className={({ isActive }) => `${navBase} ${isActive ? navActive : ""}`} data-testid="nav-ad-formats">
          <SettingsIcon size={18} /> Anzeigenformate
        </NavLink>}
        {user?.role === "admin" && (
          <>
            <div>
              <button
                onClick={() => setSettingsOpen((o) => !o)}
                className={`${navBase} w-full justify-between ${location.pathname.startsWith("/einstellungen") ? "text-white" : ""}`}
                data-testid="nav-settings"
              >
                <span className="flex items-center gap-3"><SettingsIcon size={18} /> Einstellungen</span>
                <ChevronDown size={14} className={`transition-transform ${settingsOpen ? "rotate-180" : ""}`} />
              </button>
              {settingsOpen && (
                <div className="mt-1 ml-5 pl-3 border-l border-white/10 space-y-1">
                  {[
                    ["/einstellungen", "Allgemein & E-Mail", true],
                    ["/benutzer", "Benutzer & Rechte", false],
                    ["/einstellungen/mitwirkende", "Mitwirkende", false],
                    ["/einstellungen/anzeigenformate", "Anzeigenformate", false],
                    ["/einstellungen/buchhaltung", "Buchhaltung", false],
                    ["/einstellungen/mail", "E-Mail-Versand (MS365)", false],
                    ["/mails", "Mailvorlagen", false],
                    ["/einstellungen/live", "Live-URL & Meta-Tags", false],
                    ["/einstellungen/sicherheit", "Automatischer Logout", false],
                    ["/einstellungen/benachrichtigungen", "Benachrichtigungen", false],
                    ["/einstellungen/team", "Team & Zeitplan", false],
                    ["/einstellungen/labels", "Status-Labels", false],
                    ["/einstellungen/farbprofile", "Farbprofile & Schriften", false],
                    ["/einstellungen/cloud", "Cloud-Speicher", false],
                    ["/einstellungen/footer", "Footer", false],
                    ["/einstellungen/backup", "Backup", false],
                  ].map(([to, label, end]) => (
                    <NavLink key={to} to={to} end={end}
                      className={({ isActive }) => `nav-link block px-3 py-1.5 rounded-md text-sm ${isActive ? "text-white bg-white/10" : "text-slate-400 hover:bg-white/10 hover:text-white"}`}
                      data-testid={to === "/benutzer" ? "nav-users" : to === "/mails" ? "nav-emails" : `nav-settings-${to.split("/").pop() || "general"}`}>
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </nav>

      <div className="px-3 py-2.5 border-t border-white/10 flex items-center gap-2">
        <div className="h-8 w-8 shrink-0 rounded-full nav-active text-white grid place-items-center text-xs font-semibold" title={user?.name || user?.email}>{initials}</div>
        <div className="min-w-0 flex-1 text-sm font-semibold text-white truncate" title={user?.email} data-testid="sidebar-user-name">{user?.name || user?.email}</div>
        {user?.role === "admin" && <NavLink to="/konto" title="Passwort & 2FA" className={({ isActive }) => `p-2 rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition-colors ${isActive ? "bg-white/10 text-white" : ""}`} data-testid="nav-account">
          <KeyRound size={17} />
        </NavLink>}
        <button onClick={() => { logout(); navigate("/login"); }} title="Abmelden" className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition-colors" data-testid="logout-button">
          <LogOut size={17} />
        </button>
      </div>
    </aside>
  );
}
